import { Plus, Trash2 } from "lucide-react";
import { cn } from "@util/tailwind";
import type { EvaluationCriterion } from "@type/agent";
import { Card, CardContent, CardHeader, CardTitle } from "@component/card";
import { Input } from "@component/input";
import { Button } from "@component/button";

interface EvaluationEngineProps {
    criteria: EvaluationCriterion[];
    onChange: (criteria: EvaluationCriterion[]) => void;
    disabled?: boolean;
}

export function EvaluationEngine({ criteria, onChange, disabled }: EvaluationEngineProps) {
    const totalWeight = criteria.filter((c) => c.enabled).reduce((sum, c) => sum + c.weight, 0);

    const handleChange = (index: number, updates: Partial<EvaluationCriterion>) => {
        const updated = [...criteria];
        updated[index] = { ...updated[index], ...updates };
        onChange(updated);
    };

    const addCriterion = () => {
        onChange([...criteria, { id: crypto.randomUUID(), name: "", description: "", weight: 0, enabled: true }]);
    };

    const removeCriterion = (index: number) => {
        onChange(criteria.filter((_, i) => i !== index));
    };

    return (
        <div className={disabled ? "space-y-6 pointer-events-none opacity-60" : "space-y-6"}>
            <Card>
                <CardHeader><CardTitle>Evaluation Criteria</CardTitle></CardHeader>
                <CardContent>
                    <div className="space-y-4">
                        {criteria.map((criterion, index) => (
                            <div key={criterion.id} className={cn("p-4 rounded-lg border transition-opacity", criterion.enabled ? "bg-white dark:bg-gray-900 border-gray-200 dark:border-gray-700" : "bg-gray-50 dark:bg-gray-800 border-gray-100 dark:border-gray-700 opacity-60")}>
                                <div className="flex items-start justify-between gap-4">
                                    <div className="flex-1 space-y-3">
                                        <div className="flex items-center gap-3">
                                            <input type="checkbox" checked={criterion.enabled} onChange={(e) => handleChange(index, { enabled: e.target.checked })} className="rounded border-gray-300 text-blue-600 focus:ring-blue-600" />
                                            <Input value={criterion.name} onChange={(e) => handleChange(index, { name: e.target.value })} placeholder="Criterion name" className="flex-1" />
                                        </div>
                                        <Input value={criterion.description} onChange={(e) => handleChange(index, { description: e.target.value })} placeholder="Description of what this criterion evaluates" />
                                        <div className="flex items-center gap-3">
                                            <label className="text-sm font-medium text-gray-600 w-16">Weight</label>
                                            <input type="range" min="0" max="100" value={criterion.weight} onChange={(e) => handleChange(index, { weight: parseInt(e.target.value) })} className="flex-1 accent-blue-600" disabled={!criterion.enabled} />
                                            <span className="text-sm font-semibold text-gray-700 dark:text-gray-300 w-12 text-right">{criterion.weight}%</span>
                                        </div>
                                    </div>
                                    <button onClick={() => removeCriterion(index)} className="text-gray-400 hover:text-red-500 transition-colors p-1 mt-1" aria-label={`Remove criterion ${criterion.name || index + 1}`}><Trash2 className="w-4 h-4" /></button>
                                </div>
                            </div>
                        ))}
                        <div className="flex items-center justify-between pt-2">
                            <Button variant="outline" size="sm" onClick={addCriterion}><Plus className="w-4 h-4 mr-1" />Add Criterion</Button>
                            <div className={cn("text-sm font-medium px-3 py-1 rounded-full", totalWeight === 100 ? "bg-green-50 text-green-700" : "bg-amber-50 text-amber-700")}>
                                Total Weight: {totalWeight}%{totalWeight !== 100 && " (should be 100%)"}
                            </div>
                        </div>
                    </div>
                </CardContent>
            </Card>
        </div>
    );
}
